import React, { useState, useEffect } from 'react';
import { Box, Alert, Grid } from '@mui/material';
import { TaskImageEditProps } from './utils/imageEditConstants';
import { useImageManagement } from './hooks/useImageManagement';
import { useCanvasOperations } from './hooks/useCanvasOperations';
import { CanvasArea } from './components/CanvasArea';
import { ControlPanel } from './components/ControlPanel';
import { CropDialog } from './components/CropDialog';

export default function TaskImageEdit({
  imageFile,
  onSave,
  onCancel,
  maxWidth = 800,
  maxHeight = 600,
  inModal = false,
}: TaskImageEditProps) {
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [strokeColor, setStrokeColor] = useState('#FF0000');
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [cropMode, setCropMode] = useState(false);

  const {
    backgroundImage,
    imageDimensions,
    displayDimensions,
    canvasReady,
    error,
    setError
  } = useImageManagement(imageFile, maxWidth, maxHeight);

  const {
    canvasRef,
    canUndo,
    isLoading,
    handleUndo,
    handleClear,
    handleStrokeEnd,
    handleSave,
    handleDownload
  } = useCanvasOperations(onSave, onCancel);

  useEffect(() => {
    const loadBackgroundImage = async () => {
      if (backgroundImage && canvasRef.current) {
        try {
          await canvasRef.current.clearCanvas();
          await canvasRef.current.loadPaths([]);
        } catch (error) {
          console.warn('Fehler beim Laden des Hintergrundbildes:', error);
        }
      }
    };

    loadBackgroundImage();
  }, [backgroundImage, imageDimensions]);

  const onSaveClick = () => {
    handleSave(
      backgroundImage,
      imageDimensions,
      displayDimensions,
      strokeColor,
      strokeWidth,
      setError
    );
  };

  const onDownloadClick = () => {
    handleDownload(backgroundImage, imageDimensions, setError);
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      overflow: 'hidden',
      position: 'relative',
      background: '#ffffff'
    }}>
      {error && (
        <Alert severity='error' sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      {/* Canvas Area - Takes remaining space */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden',
        position: 'relative',
        minHeight: 0
      }}>
        <CanvasArea
          canvasRef={canvasRef}
          backgroundImage={backgroundImage}
          displayDimensions={displayDimensions}
          canvasReady={canvasReady}
          strokeWidth={strokeWidth}
          strokeColor={strokeColor}
          onStrokeEnd={handleStrokeEnd}
        />
      </Box>

      {/* Fixed Bottom Controls - Only if inModal */}
      {inModal && (
        <Box sx={{
          position: 'relative',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          padding: '16px',
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(8px)',
          borderTop: '1px solid rgba(59, 130, 246, 0.2)',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
        }}>
          <ControlPanel
            strokeWidth={strokeWidth}
            strokeColor={strokeColor}
            canUndo={canUndo}
            isLoading={isLoading}
            onStrokeWidthChange={setStrokeWidth}
            onStrokeColorChange={setStrokeColor}
            onUndo={handleUndo}
            onClear={handleClear}
            onSave={onSaveClick}
            onCancel={onCancel}
            isModalFooter={true}
          />
        </Box>
      )}

      {/* Full Screen Fixed Controls - Only if NOT inModal */}
      {!inModal && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          padding: '12px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          borderRadius: '16px 16px 0 0',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderBottom: 'none',
          minWidth: '320px',
          maxWidth: '90vw'
        }}>
          <ControlPanel
            strokeWidth={strokeWidth}
            strokeColor={strokeColor}
            canUndo={canUndo}
            isLoading={isLoading}
            onStrokeWidthChange={setStrokeWidth}
            onStrokeColorChange={setStrokeColor}
            onUndo={handleUndo}
            onClear={handleClear}
            onSave={onSaveClick}
            onCancel={onCancel}
            isFixedBottom={true}
          />
        </Box>
      )}
    </Box>
  );
}
